import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Card, Typography } from '../ui';
import { Spinner } from '../../Spinner';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { GroupCard } from './GroupCard';
import { GroupsEmpty } from './GroupsEmpty';
import { MAX_LIVE_GROUPS } from './constants';
import styles from './GroupsSection.module.css';

// The "@symphony" handle inside the explainer is set apart from the sentence around it —
// semibold, in the primary ink rather than the secondary the rest of the line wears (Figma
// 11105-127668). It is the one literal string the reader has to type into WhatsApp, so it
// reads as a token, not prose. Both native apps already split the same sentence this way
// (`WhatsappScreen.swift` / `WhatsappScreen.kt`); this brings web to parity.
//
// The split is on the interpolated handle rather than a markup tag so no locale has to carry
// one: a translation that drops or reorders `{mention}` simply renders as plain text.
function MentionHint({ text, mention }: { text: string; mention: string }) {
  const at = text.indexOf(mention);
  if (at === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className={styles.mention}>{mention}</span>
      {text.slice(at + mention.length)}
    </>
  );
}

// The Groups block (Figma 14077-197662): the section head — the label and the "tag @<product>"
// explainer — over the stacked group tiles. New Group lives in the modal footer, not here. Owns
// nothing but presentation; the panel state lives on the screen.
export function GroupsSection({
  groups,
  loading,
  error,
  selectedGroupId,
  onNewGroup,
  onOpenGroup,
}: {
  groups: WhatsappGroup[];
  loading: boolean;
  error: boolean;
  selectedGroupId: string | null;
  onNewGroup: () => void;
  onOpenGroup: (group: WhatsappGroup) => void;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const mention = `@${productName.toLowerCase()}`;
  const atCap = groups.length >= MAX_LIVE_GROUPS;
  const empty = !loading && !error && groups.length === 0;

  return (
    <section className={styles.section}>
      <div className={styles.headText}>
        <Typography variant="h2" color="strong">
          {t('settings.whatsapp.groups.section')}
        </Typography>
        <Typography variant="p2" color="secondary">
          <MentionHint text={t('settings.whatsapp.groups.hint', { productName, mention })} mention={mention} />
        </Typography>
      </div>

      {atCap && (
        <Typography variant="p2" color="muted">
          {t('settings.whatsapp.groups.capReached')}
        </Typography>
      )}

      {loading && (
        <Card className={styles.state}>
          <Spinner />
        </Card>
      )}
      {error && (
        <Card className={styles.state}>
          <Typography variant="p2" color="secondary">
            {t('settings.whatsapp.error')}
          </Typography>
        </Card>
      )}
      {empty && <GroupsEmpty onNewGroup={onNewGroup} disabled={atCap} />}

      {groups.length > 0 && (
        <div className={styles.grid}>
          {groups.map((group) => (
            <GroupCard key={group.id} group={group} selected={group.id === selectedGroupId} onOpen={() => onOpenGroup(group)} />
          ))}
        </div>
      )}
    </section>
  );
}
